import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LayoutService } from '../../shared-services/layout.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';

export interface UpdateNavigationCardAction {
  icon?: string;
  iconUrl?: string;
  label?: string;
  color?: string;
  backgroundColor?: string;
  navigateTo?: string;
  queryParams?: Record<string, any>;
}

@Component({
  selector: 'app-update-navigation-card',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './update-navigation-card.component.html',
  styleUrl: './update-navigation-card.component.scss',
})
export class UpdateNavigationCardComponent implements OnInit, OnDestroy {
  @Input() data: any;
  layoutDensity: 'normal' | 'compact' | 'dense' = 'normal';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private layoutService: LayoutService,
    private responsiveLayoutService: ResponsiveLayoutService,
  ) {}

  ngOnInit(): void {
    combineLatest([
      this.layoutService.activeLayoutId$,
      this.layoutService.manifest$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([layoutId, manifest]) => {
        const layout = manifest?.layouts.find((item) => item.id === layoutId);
        const widestRow = layout?.rowCounts?.length
          ? Math.max(...layout.rowCounts)
          : 1;

        this.layoutDensity =
          widestRow >= 4 ? 'dense' : widestRow >= 3 ? 'compact' : 'normal';
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get actions(): UpdateNavigationCardAction[] {
    return Array.isArray(this.data?.actions) ? this.data.actions : [];
  }

  get resolvedHeight(): string {
    return this.responsiveLayoutService.resolveWidgetHeight(this.data);
  }

  get cardStyles(): Record<string, string> {
    return {
      '--card-background': this.data?.backgroundColor || '#ffffff',
      '--card-border': this.data?.borderColor || '#d9dce1',
      '--primary-color': this.data?.primaryColor || '#dc2626',
      '--primary-soft': this.data?.primaryBackgroundColor || '#fff1f2',
      '--title-color': this.data?.titleColor || '#202124',
      '--text-color': this.data?.textColor || '#4b5563',
      '--divider-color': this.data?.dividerColor || '#e5e7eb',
    };
  }

  navigate(config: any = this.data): void {
    if (this.data?.disabled) return;

    const route = String(config?.navigateTo || '').trim();
    if (route) {
      this.router.navigate([route], {
        queryParams: config?.queryParams,
        state: config?.navigationState,
      });
      return;
    }

    const externalUrl = String(config?.externalUrl || '').trim();
    if (externalUrl) {
      window.open(externalUrl, config?.target || '_self');
    }
  }

  onCardKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.navigate();
  }

  onActionClick(event: Event, action: UpdateNavigationCardAction): void {
    event.stopPropagation();
    this.navigate(action);
  }

  isImageIcon(iconUrl: unknown): boolean {
    return typeof iconUrl === 'string' && iconUrl.trim().length > 0;
  }
}
